import { DataStatus, isFinished, isLoading } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import * as datasourcesAPI from '../api/datasourcesAPI';
import type { Integration } from './types';

interface State {
  integrations: Integration[];
  status: DataStatus;
}

const initialState: State = {
  integrations: [],
  status: DataStatus.idle,
};

export const datasourcesSlice = createAppSlice({
  name: 'datasources',
  initialState,
  reducers: (create) => ({
    setupIntegrations: create.asyncThunk(
      (_: void) => {
        return datasourcesAPI.getIntegrations();
      },
      {
        pending: (state) => {
          state.status = DataStatus.loading;
        },
        fulfilled: (state, action) => {
          state.integrations = action.payload;
          state.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.status = DataStatus.error;
        },
        options: {
          condition: (_, { getState }) => {
            const state = getState() as RootState;

            if (isLoading(state.datasources.status)) {
              return false;
            }
          },
        },
      },
    ),
    addOrUpdateIntegration: create.reducer<Integration>((state, action) => {
      let updated = false;

      state.integrations = state.integrations.map((integration) => {
        if (integration.id === action.payload.id) {
          updated = true;

          return action.payload;
        }

        return integration;
      });

      if (!updated) {
        state.integrations.push(action.payload);
      }
    }),
    deleteIntegration: create.asyncThunk<string>(
      (id) => {
        return datasourcesAPI.deleteIntegration(id);
      },
      {
        fulfilled: (state, action) => {
          state.integrations = state.integrations.filter((integration) => {
            return integration.id !== action.meta.arg;
          });
        },
      },
    ),
  }),
  selectors: {
    selectIntegrations: (state) => {
      return state.integrations;
    },
    selectIsLoading: (state) => {
      return isLoading(state.status);
    },
    selectIsFinished: (state) => {
      return isFinished(state.status);
    },
  },
});

export const { setupIntegrations, addOrUpdateIntegration, deleteIntegration } =
  datasourcesSlice.actions;
export const { selectIsFinished, selectIsLoading, selectIntegrations } =
  datasourcesSlice.selectors;
